import { MenuCategoryProxy, MenuItemPortionProxy, MenuItemProxy } from "@me-team/host/main/graphql/types";
import { DeepPartial } from "@apollo/client/utilities";
import {
    DishesInterfaceForModal,
    EditMenuCategoryInterface,
    SelectedCategoryStateInterface,
} from "../interfaces/interface";
import { CategoryInterface } from "@me-pos/service-modal/dist/src/types/interfaces";
import { SubcategoryInterface } from "../../Services/CreateService/type";

export class EditMenuService {
    public static hasMissingTranslation = (
        translatedValue?: string | null,
        originValue?: string | null,
        isTranslationMode = false
    ): boolean => {
        if (!isTranslationMode) {
            return false;
        }

        const normalizedTranslatedValue = (translatedValue ?? "").trim();
        const normalizedOriginValue = (originValue ?? "").trim();

        return !normalizedTranslatedValue && Boolean(normalizedOriginValue);
    };

    public static resolveTranslatedName = (translatedValue?: string | null, originValue?: string | null): string => {
        const normalizedTranslatedValue = (translatedValue ?? "").trim();
        if (normalizedTranslatedValue) {
            return normalizedTranslatedValue;
        }

        return (originValue ?? "").trim();
    };

    public static categoryBaseInit = (
        categories: DeepPartial<MenuCategoryProxy>[] | undefined | null
    ): EditMenuCategoryInterface[] => {
        return (categories ?? []).map((category) => ({
            ...category,
            isActive: false,
            childCategoriesList: (category.childCategories ?? []).map((childCat) => ({
                ...childCat,
                isActive: false,
            })),
        }));
    };

    public static applyActive = (
        list: EditMenuCategoryInterface[],
        selectedCategory: SelectedCategoryStateInterface
    ): EditMenuCategoryInterface[] => {
        return list.map((category) => {
            const isCategoryActive = selectedCategory.categoryId === category.id && !selectedCategory.childCategoryId;

            const childCategoriesList = (category.childCategoriesList ?? []).map((childCat) => ({
                ...childCat,
                isActive:
                    selectedCategory.categoryId === category.id && selectedCategory.childCategoryId === childCat.id,
            }));

            return {
                ...category,
                isActive: isCategoryActive,
                childCategoriesList,
            };
        });
    };

    public static categoryModuleCreator = (
        categoryList: DeepPartial<MenuCategoryProxy[]>,
        categoryId: number | null,
        subcategoryId: number | null,
        isTranslationMode = false
    ): CategoryInterface[] => {
        return categoryList.map((category) => {
            const hasMissingTranslation = this.hasMissingTranslation(
                category.name,
                category.nameOrigin,
                isTranslationMode
            );

            return {
                id: category.id,
                name: this.resolveTranslatedName(category.name, category.nameOrigin),
                hasMissingTranslation,
                isMainCategoryDisabled: category.isExistsInMenu,
                isOpen: !!(category.id === categoryId && subcategoryId),
                isPicked: !!(category.id === categoryId && !subcategoryId),
                isSubcategoryPiked: !!(category.id === categoryId && subcategoryId),
                subcategoryList: this.subcategoryModuleCreator(
                    category.childCategories,
                    subcategoryId,
                    isTranslationMode
                ),
            } as CategoryInterface;
        });
    };

    public static subcategoryModuleCreator = (
        subcategories: DeepPartial<MenuCategoryProxy[]>,
        currentSubcategoryId: number | null,
        isTranslationMode = false
    ): SubcategoryInterface[] => {
        return subcategories.map((subcategory) => {
            const hasMissingTranslation = this.hasMissingTranslation(
                subcategory.name,
                subcategory.nameOrigin,
                isTranslationMode
            );

            return {
                id: subcategory.id,
                name: this.resolveTranslatedName(subcategory.name, subcategory.nameOrigin),
                hasMissingTranslation,
                isPicked: subcategory.id === currentSubcategoryId,
            } as SubcategoryInterface;
        });
    };

    public static portionsValueView = <K extends "price" | "volume">(
        portions: DeepPartial<MenuItemPortionProxy>[],
        field: K
    ): string => {
        if (!portions || portions.length === 0) {
            return "";
        }

        const values = portions.map((p) => p[field]).filter((v): v is number => typeof v === "number");

        if (values.length === 0) {
            return "";
        }

        if (values.length === 1) {
            return String(values[0]);
        }

        return values.join(" / ");
    };

    public static normalizeSearch = (value: string) => {
        return value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
    };

    public static initCategoriesInterface = (
        categoriesList: DeepPartial<MenuCategoryProxy[]>,
        isTranslationMode = false
    ): CategoryInterface[] => {
        return categoriesList.map((category) => {
            const hasMissingTranslation = this.hasMissingTranslation(
                category.name,
                category.nameOrigin,
                isTranslationMode
            );

            return {
                id: category.id,
                name: this.resolveTranslatedName(category.name, category.nameOrigin),
                hasMissingTranslation,
                isMainCategoryDisabled: category.isExistsInMenu,
                isPicked: false,
                isOpen: false,
                isSubcategoryPiked: false,
                subcategoryList: (category.childCategories ?? []).map((childCat) => {
                    const childHasMissingTranslation = this.hasMissingTranslation(
                        childCat.name,
                        childCat.nameOrigin,
                        isTranslationMode
                    );

                    return {
                        id: childCat.id,
                        name: this.resolveTranslatedName(childCat.name, childCat.nameOrigin),
                        hasMissingTranslation: childHasMissingTranslation,
                        isPicked: false,
                    } as SubcategoryInterface;
                }),
            } as CategoryInterface;
        });
    };

    public static initDishesInterface = (dishesList: DeepPartial<MenuItemProxy[]>): DishesInterfaceForModal[] => {
        return (
            dishesList.map((dish) => {
                return {
                    ...dish,
                    isPiked: false,
                    isAvailableForCurrentMenu: dish.isAvailableForCurrentMenu,
                };
            }) || []
        );
    };
}
